import React, { useState, useEffect, useMemo } from "react";

// Imports de bibliotecas
import { Container, Row, Col, Tabs, Tab, Card } from "react-bootstrap";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import GaugeChart from 'react-gauge-chart';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css'; // Importe o CSS do slider

// Simulação da API - substitua pela sua importação real
import { mat_acumuladas_unidade } from "../../services/api";

// Registro dos componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const Corrida_Unidades = () => {
    // === ESTADOS ===
    const [rawData, setRawData] = useState([]);
    const [dateRange, setDateRange] = useState([1, 1]);
    const [maxDays, setMaxDays] = useState(1);
    const [activeTab, setActiveTab] = useState('acumulada');

    // === BUSCA DE DADOS ===
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await mat_acumuladas_unidade();
                const days = response.length;
                setRawData(response);
                setMaxDays(days);
                // CORREÇÃO: Inicia o slider com o período completo (Dia 1 até o último dia)
                setDateRange([0, days]);
            } catch (error) {
                console.error("Erro ao buscar dados:", error);
            }
        };
        fetchData();
    }, []);

    // === CÁLCULOS MEMOIZADOS (Performance) ===
    // Filtra os dados brutos com base no período selecionado
    const filteredData = useMemo(() => {
        return rawData.filter(item => {
            // CORREÇÃO: Usa 'dia_matricula' e ajusta para base 1 (para corresponder ao slider)
            const day = item.dia_matricula + 1;
            return day >= dateRange[0] && day <= dateRange[1];
        });
    }, [rawData, dateRange]);

    // Calcula as médias diárias para o período filtrado
    const periodAverages = useMemo(() => {
        if (filteredData.length === 0) return {};
        // const totals = { 'BR': 0, 'MD': 0, 'SC': 0, 'CD': 0, 'TQ': 0, 'NP': 0, 'SP': 0, 'BT': 0, 'CG': 0, 'MC': 0, 'IG': 0, 'FG': 0, 'RB': 0 };
        const totals = { 'BT': 0, 'BR': 0, 'CG': 0, 'CD': 0, 'FG': 0, 'IG': 0, 'MD': 0, 'MC': 0, 'NP': 0, 'RB': 0, 'SC': 0, 'SP': 0, 'TQ': 0 };
        filteredData.forEach(item => {
            for (const unit in totals) {
                totals[unit] += parseInt(item[unit.toLowerCase()]) || 0;
            }
        });
        const numberOfDays = filteredData.length;
        const averages = {};
        for (const unit in totals) {
            averages[unit] = totals[unit] / numberOfDays;
        }
        return averages;
    }, [filteredData]);


    // Determina o valor máximo para os gauges
    const gaugeMax = useMemo(() => {
        const values = Object.values(periodAverages);
        if (values.length === 0) return 1;
        return Math.max(...values);
    }, [periodAverages]);

    // Prepara os dados para o gráfico de linha (Curva Acumulada)
    const lineChartData = useMemo(() => {
        // CORREÇÃO: Usa 'dia_matricula + 1' para o rótulo ser consistente
        const labels = filteredData.map(item => `Dia ${item.dia_matricula + 1}`);
        const siglas = ['BR', 'MD', 'SC', 'CD', 'TQ', 'NP', 'SP', 'BT', 'CG', 'MC', 'IG', 'FG', 'RB'];

        let acumulador = {};
        let unitData = {};
        siglas.forEach(sigla => {
            acumulador[sigla] = 0;
            unitData[sigla] = [];
        });

        filteredData.forEach(item => {
            siglas.forEach(sigla => {
                const valor = parseInt(item[sigla.toLowerCase()] ?? "0", 10) || 0;
                acumulador[sigla] += valor;
                unitData[sigla].push(acumulador[sigla]);
            });
        });

        return {
            labels,
            datasets: [
                { label: 'BT', data: unitData['BT'], borderColor: '#9400d3', backgroundColor: '#9400d3' },
                { label: 'BR', data: unitData['BR'], borderColor: '#ef4444', backgroundColor: '#ef4444' },
                { label: 'CG', data: unitData['CG'], borderColor: '#ff1493', backgroundColor: '#ff1493' },
                { label: 'CD', data: unitData['CD'], borderColor: '#eab308', backgroundColor: '#eab308' },
                { label: 'FG', data: unitData['FG'], borderColor: '#ffa500', backgroundColor: '#ffa500' },
                { label: 'IG', data: unitData['IG'], borderColor: '#32cd32', backgroundColor: '#32cd32' },
                { label: 'MD', data: unitData['MD'], borderColor: '#3b82f6', backgroundColor: '#3b82f6' },
                { label: 'MC', data: unitData['MC'], borderColor: '#1e90ff', backgroundColor: '#1e90ff' },
                { label: 'NP', data: unitData['NP'], borderColor: '#ff7f50', backgroundColor: '#ff7f50' },
                { label: 'RB', data: unitData['RB'], borderColor: '#8b0000', backgroundColor: '#8b0000' },
                { label: 'SC', data: unitData['SC'], borderColor: '#10b981', backgroundColor: '#10b981' },
                { label: 'SP', data: unitData['SP'], borderColor: '#00ced1', backgroundColor: '#00ced1' },
                { label: 'TQ', data: unitData['TQ'], borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' },
            ]
        };
    }, [filteredData]);

    // Prepara os dados para o gráfico de barras (Matrículas por Dia)
    const barChartData = useMemo(() => {
        // CORREÇÃO: Usa 'dia_matricula + 1' para o rótulo ser consistente
        const labels = filteredData.map(item => `Dia ${item.dia_matricula + 1}`);
        return {
            labels,
            datasets: [
                { label: 'BT', data: filteredData.map(d => parseInt(d.bt) || 0), backgroundColor: '#9400d3' },
                { label: 'BR', data: filteredData.map(d => parseInt(d.br) || 0), backgroundColor: '#ef4444' },
                { label: 'CG', data: filteredData.map(d => parseInt(d.cg) || 0), backgroundColor: '#ff1493' },
                { label: 'CD', data: filteredData.map(d => parseInt(d.cd) || 0), backgroundColor: '#eab308' },
                { label: 'FG', data: filteredData.map(d => parseInt(d.fg) || 0), backgroundColor: '#ffa500' },
                { label: 'IG', data: filteredData.map(d => parseInt(d.ig) || 0), backgroundColor: '#32cd32' },
                { label: 'MD', data: filteredData.map(d => parseInt(d.md) || 0), backgroundColor: '#3b82f6' },
                { label: 'MC', data: filteredData.map(d => parseInt(d.mc) || 0), backgroundColor: '#1e90ff' },
                { label: 'NP', data: filteredData.map(d => parseInt(d.np) || 0), backgroundColor: '#ff7f50' },
                { label: 'RB', data: filteredData.map(d => parseInt(d.rb) || 0), backgroundColor: '#8b0000' },
                { label: 'SC', data: filteredData.map(d => parseInt(d.sc) || 0), backgroundColor: '#10b981' },
                { label: 'SP', data: filteredData.map(d => parseInt(d.sp) || 0), backgroundColor: '#00ced1' },
                { label: 'TQ', data: filteredData.map(d => parseInt(d.tq) || 0), backgroundColor: '#8b5cf6' },
            ]
        };
    }, [filteredData]);

    // === OPÇÕES DOS GRÁFICOS ===
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: { x: { ticks: { maxTicksLimit: 15 } } }
    };

    if (rawData.length === 0) {
        return <Container><p>Carregando dados...</p></Container>;
    }

    // === RENDERIZAÇÃO ===
    return (
        <Container fluid className="p-4">
            <Row>
                <Col md={4}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Painel de Controle</Card.Title>
                            <hr />
                            <h6>Período de Análise</h6>
                            <div className="px-2">
                                <Slider
                                    range
                                    min={1}
                                    max={maxDays}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    allowCross={false}
                                />
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                                <span>Dia {dateRange[0]}</span>
                                <span>Dia {dateRange[1]}</span>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Body>
                            <Card.Title>Matriculas por dia no Período</Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">Matriculas por dia</Card.Subtitle>
                            <hr />
                            <Row>
                                {Object.entries(periodAverages).map(([unit, avg], idx) => (
                                    <Col key={unit} xs={12} sm={6} md={2} lg={2} xl={2} className="text-center mb-3" style={{ flex: '0 0 20%', maxWidth: '20%' }}>
                                        <strong>{unit} : {avg.toFixed(1)}</strong>
                                        <div style={{ maxWidth: '140px', margin: '0 auto' }}>
                                            <GaugeChart
                                                id={`gauge-${unit}`}
                                                nrOfLevels={20}
                                                colors={['#ef4444', '#f59e0b', '#10b981']}
                                                arcWidth={0.5}
                                                percent={gaugeMax > 0 ? avg / gaugeMax : 0}
                                                textColor="#343a40"
                                                formatTextValue={() => avg.toFixed(1)}
                                            />
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={8}>
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="graficos-principais-tabs" className="mb-3">
                        <Tab eventKey="acumulada" title="Curva Acumulada">
                            <div style={{ height: '65vh' }}>
                                <Line options={chartOptions} data={lineChartData} />
                            </div>
                        </Tab>
                        <Tab eventKey="por_dia" title="Matrículas por Dia">
                            <div style={{ height: '65vh' }}>
                                <Bar options={{ ...chartOptions, scales: { ...chartOptions.scales, x: { ...chartOptions.scales.x, stacked: false }, y: { stacked: false } } }} data={barChartData} />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Container>
    );
};

export default Corrida_Unidades;